import { useMemo, useState } from 'react'
import { AlertCircle, Upload, X } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { usePricingImport } from '../hooks/use-pricing-import'
import { PricingPreviewTable } from '../components/pricing-preview-table'
import { PricingResult } from '../components/pricing-result'
import { buildPricingItemSchema } from '../types'
import type { PricingItem, ValidationError } from '../types'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Spinner } from '@/components/ui/spinner'
import { ApiError } from '@/api/types'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import { FileUploadZone } from '@/shared/components/file-upload-zone'
import { PermissionGate } from '@/shared/components/permission-gate'
import { ACTIONS } from '@/shared/constants/actions'

export function PricingImportPage() {
  const { t: tPricing } = useTranslation('pricing')
  const { t: tCommon } = useTranslation('common')
  const [restaurantId, setRestaurantId] = useState<string>('')
  const [filename, setFilename] = useState<string>('')
  const [items, setItems] = useState<Array<PricingItem>>([])
  const [errors, setErrors] = useState<Array<ValidationError>>([])

  const { data: restaurantsData } = useRestaurants()
  const mutation = usePricingImport()
  const pricingItemSchema = useMemo(
    () => buildPricingItemSchema(tPricing),
    [tPricing],
  )

  const handleFileSelect = (content: string, selectedFilename: string) => {
    try {
      const parsed = JSON.parse(content)

      // Validate structure
      if (!parsed || typeof parsed !== 'object') {
        toast.error(tPricing('import.invalidJsonObject'))
        return
      }

      if (!Array.isArray(parsed.items)) {
        toast.error(tPricing('import.invalidJsonItems'))
        return
      }

      if (parsed.items.length === 0) {
        toast.error(tPricing('import.noItems'))
        return
      }

      // Validate each item
      const validatedItems: Array<PricingItem> = []
      const validationErrors: Array<ValidationError> = []

      parsed.items.forEach((item: unknown, index: number) => {
        const result = pricingItemSchema.safeParse(item)
        if (result.success) {
          validatedItems.push(result.data)
        } else {
          result.error.issues.forEach((err) => {
            validationErrors.push({
              row: index,
              field: err.path.join('.'),
              message: err.message,
            })
          })
          // Add item anyway for preview (with potential defaults)
          validatedItems.push({
            sku: (item as any).sku || '',
            priceGrossCents: (item as any).priceGrossCents || 0,
          })
        }
      })

      setFilename(selectedFilename)
      setItems(validatedItems)
      setErrors(validationErrors)

      if (validationErrors.length > 0) {
        toast.warning(
          tPricing('import.validationErrors', {
            count: validationErrors.length,
          }),
        )
      } else {
        toast.success(
          tPricing('import.loadedItems', { count: validatedItems.length }),
        )
      }
    } catch (error) {
      toast.error(tPricing('import.parseFailed'))
      console.error(error)
    }
  }

  const handleClear = () => {
    setFilename('')
    setItems([])
    setErrors([])
  }

  const handleImport = () => {
    if (!restaurantId) {
      toast.error(tPricing('import.selectRestaurant'))
      return
    }

    if (errors.length > 0) {
      toast.error(tPricing('import.fixErrors'))
      return
    }

    if (items.length === 0) {
      toast.error(tPricing('import.noItemsToImport'))
      return
    }

    mutation.mutate(
      {
        restaurantId: parseInt(restaurantId),
        items,
      },
      {
        onSuccess: (data) => {
          toast.success(
            tPricing('import.updatedPrices', { count: data.updated }),
          )
          handleClear()
        },
        onError: () => {
          toast.error(tPricing('import.importFailed'))
        },
      },
    )
  }

  const hasValidationErrors = errors.length > 0
  const canImport =
    restaurantId &&
    items.length > 0 &&
    !hasValidationErrors &&
    !mutation.isPending

  return (
    <PermissionGate action={ACTIONS.PRICING_IMPORT}>
      <div>
        <div className="mb-6">
          <h1 className="text-3xl font-bold">{tPricing('import.title')}</h1>
          <p className="text-muted-foreground mt-2">
            {tPricing('import.subtitle')}
          </p>
        </div>

        {mutation.data && <PricingResult result={mutation.data} />}

        {mutation.error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{tPricing('import.importFailedTitle')}</AlertTitle>
            <AlertDescription>
              {(() => {
                const err = mutation.error
                if (err instanceof ApiError && err.code === 'unknown_skus') {
                  const unknownSkus = Array.isArray((err.data as any)?.items)
                    ? ((err.data as any).items as Array<string>)
                    : []
                  return (
                    <div>
                      <div className="mb-2">
                        {tPricing('import.unknownSkusTitle')}
                      </div>
                      {unknownSkus.length > 0 && (
                        <div className="text-xs">
                          {tPricing('import.unknownSkusLabel')}:{' '}
                          <span className="font-mono">
                            {unknownSkus.join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                  )
                }
                return err instanceof Error
                  ? err.message
                  : tPricing('import.unknownError')
              })()}
            </AlertDescription>
          </Alert>
        )}

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{tPricing('import.restaurantCardTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="restaurant">
                {tPricing('import.restaurantLabel')}
              </Label>
              <Select value={restaurantId} onValueChange={setRestaurantId}>
                <SelectTrigger id="restaurant">
                  <SelectValue
                    placeholder={tPricing('import.restaurantPlaceholder')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {restaurantsData?.items.map((restaurant) => (
                    <SelectItem
                      key={restaurant.id}
                      value={restaurant.id.toString()}
                    >
                      {restaurant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {items.length === 0 ? (
          <FileUploadZone
            onFileSelect={handleFileSelect}
            disabled={mutation.isPending}
          />
        ) : (
          <Card className="mb-6">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>{tPricing('import.previewTitle')}</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {tPricing('import.previewSummary', {
                      filename,
                      count: items.length,
                    })}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClear}
                  disabled={mutation.isPending}
                >
                  <X className="w-4 h-4 mr-2" />
                  {tPricing('import.clear')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {hasValidationErrors && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>{tPricing('import.validationTitle')}</AlertTitle>
                  <AlertDescription>
                    {tPricing('import.validationDescription', {
                      count: errors.length,
                    })}
                  </AlertDescription>
                </Alert>
              )}

              <PricingPreviewTable items={items} errors={errors} />

              <div className="mt-6 flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={handleClear}
                  disabled={mutation.isPending}
                >
                  {tCommon('cancel')}
                </Button>
                <Button onClick={handleImport} disabled={!canImport}>
                  {mutation.isPending && <Spinner className="w-4 h-4 mr-2" />}
                  <Upload className="w-4 h-4 mr-2" />
                  {tCommon('import')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>{tPricing('import.jsonFormatTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted p-4 rounded-md overflow-x-auto">
              {JSON.stringify(
                {
                  items: [
                    { sku: 'KB-BOWL-01', priceGrossCents: 1090 },
                    { sku: 'KB-EXTRA-01', priceGrossCents: 250 },
                  ],
                },
                null,
                2,
              )}
            </pre>
            <p className="text-xs text-muted-foreground mt-2">
              {tPricing('import.jsonFormatNote')}
            </p>
          </CardContent>
        </Card>
      </div>
    </PermissionGate>
  )
}
